import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Lock, Shield } from "lucide-react";
import Footer from "@/components/Footer";
import { userTokenStore } from "@/services/sellerTokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [togglingTwoFactor, setTogglingTwoFactor] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    // Phase-5: memory JWT — refresh from httpOnly cookie on load.
    let cancelled = false;
    const hydrate = async () => {
      let token = userTokenStore.getAccessToken();
      if (!token) {
        try {
          const r = await axios.post(`${API}/auth/user/refresh`, {}, { withCredentials: true });
          token = r.data?.access_token || null;
          if (token) userTokenStore.setSession(token, r.data?.user || null);
        } catch (_) { /* not signed in */ }
      }
      if (cancelled) return;
      if (!token) {
        toast.error("Please login first");
        navigate("/user");
        return;
      }
      fetchUserInfo();
    };
    hydrate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeader = () => {
    const token = userTokenStore.getAccessToken();
    return {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    };
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/user/me`, getAuthHeader());
      setUser(response.data);
      setTwoFactorEnabled(response.data.two_factor_enabled || false);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/user/logout`, {}, { withCredentials: true });
    } catch (_) { /* best-effort */ }
    userTokenStore.clear();
    try {
      localStorage.removeItem("license_data");
      localStorage.removeItem("user_email");
    } catch (_) { /* ignore */ }
    navigate("/user");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords don't match!");
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      setLoading(true);
      await axios.patch(
        `${API}/auth/user/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        getAuthHeader()
      );

      toast.success("Password changed successfully! Check your email for confirmation.");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();

    if (!newEmail) {
      toast.error("Please enter a new email address");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.patch(
        `${API}/auth/user/update-email`,
        { new_email: newEmail },
        getAuthHeader()
      );

      toast.success("Email updated! Check both emails for confirmation. Please login with your new email.");
      
      // Logout after email change
      setTimeout(() => {
        handleLogout();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTwoFactor = async () => {
    try {
      setTogglingTwoFactor(true);
      const newStatus = !twoFactorEnabled;
      
      await axios.patch(
        `${API}/auth/user/toggle-2fa?enabled=${newStatus}`,
        {},
        getAuthHeader()
      );

      setTwoFactorEnabled(newStatus);
      toast.success(
        newStatus
          ? "Two-factor authentication enabled! A verification code will be sent to your email on your next login."
          : "Two-factor authentication disabled."
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle 2FA");
    } finally {
      setTogglingTwoFactor(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/user")}
              className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-block"
            >
              ← Back to Dashboard
            </button>
            <h2 className="text-3xl font-bold text-gray-900">
              Account Settings
            </h2>
            <p className="text-gray-600">Manage your profile and security</p>
          </div>

        <div className="space-y-6">
          {/* Account Information */}
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <User className="w-6 h-6" style={{ color: '#0073ea' }} />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Full Name</p>
                  <p className="font-semibold text-gray-800">{user.full_name}</p>
                </div>
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-800">{user.email}</p>
                </div>
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Account ID</p>
                  <p className="font-mono text-xs text-gray-800">{user.id}</p>
                </div>
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">License Status</p>
                  <p className="font-semibold text-gray-800">
                    {user.license_key ? (
                      <span className="text-green-600">✓ Active</span>
                    ) : (
                      <span className="text-gray-500">No License</span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Lock className="w-6 h-6" style={{ color: '#0073ea' }} />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, current_password: e.target.value })
                    }
                    required
                    placeholder="Enter current password"
                    data-testid="user-current-password"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, new_password: e.target.value })
                    }
                    required
                    minLength={6}
                    placeholder="Enter new password (min 6 characters)"
                    data-testid="user-new-password"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, confirm_password: e.target.value })
                    }
                    required
                    placeholder="Confirm new password"
                    data-testid="user-confirm-password"
                    className="border-gray-300 focus:border-blue-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 text-white font-semibold transition-all duration-200"
                  data-testid="user-change-password-btn"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                  onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                >
                  {loading ? "Changing..." : "Change Password"}
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Update Email */}
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Mail className="w-6 h-6" style={{ color: '#0073ea' }} />
                Update Email Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateEmail} className="space-y-4">
                <div>
                  <Label htmlFor="current-email">Current Email</Label>
                  <Input
                    id="current-email"
                    type="email"
                    value={user.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                <div>
                  <Label htmlFor="new-email">New Email Address</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    placeholder="Enter new email address"
                    data-testid="user-new-email"
                    className="border-gray-300 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ You'll be logged out and need to login with your new email
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 text-white font-semibold transition-all duration-200"
                  data-testid="user-update-email-btn"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#005bb5')}
                  onMouseLeave={(e) => !e.target.disabled && (e.target.style.backgroundColor = '#0073ea')}
                >
                  {loading ? "Updating..." : "Update Email"}
                </button>
              </form>
            </CardContent>
          </Card>

          {/* Two-Factor Authentication */}
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Shield className="w-6 h-6" style={{ color: '#0073ea' }} />
                Two-Factor Authentication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">
                        {twoFactorEnabled ? "🔒 2FA Enabled" : "🔓 2FA Disabled"}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {twoFactorEnabled
                          ? "Your account is protected with two-factor authentication. A verification code will be sent to your email each time you login."
                          : "Enable two-factor authentication for enhanced security. You'll receive a verification code at your email when you login."}
                      </p>
                    </div>
                    <button
                      onClick={handleToggleTwoFactor}
                      disabled={togglingTwoFactor}
                      className={`ml-4 px-4 py-2 text-white font-medium transition-colors ${
                        twoFactorEnabled
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                      data-testid="user-toggle-2fa-btn"
                    >
                      {togglingTwoFactor
                        ? "Processing..."
                        : twoFactorEnabled
                        ? "Disable"
                        : "Enable"}
                    </button>
                  </div>
                </div>
                
                {twoFactorEnabled && (
                  <div className="bg-blue-50 border border-blue-200 p-4">
                    <div className="flex items-start gap-2">
                      <div style={{ color: '#0073ea' }} className="text-xl">ℹ️</div>
                      <div className="flex-1">
                        <p className="text-sm text-blue-800">
                          <strong>Important:</strong> When 2FA is enabled, you'll receive a verification code at your email address ({user?.email}) each time you login.
                          The code expires after 10 minutes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Security Tips */}
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Shield className="w-6 h-6" style={{ color: '#0073ea' }} />
                Security Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                  <span><strong>Use a strong password:</strong> At least 6 characters with mixed case and numbers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                  <span><strong>Enable 2FA:</strong> Add an extra layer of security to your account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                  <span><strong>Keep email secure:</strong> Use a trusted email provider</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                  <span><strong>Never share credentials:</strong> Keep your password private</span>
                </li>
                <li className="flex items-start gap-2">
                  <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                  <span><strong>Logout when done:</strong> Always logout on shared devices</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserSettings;
